import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import Button from "../components/ui/Button";
import { sendOrderNotifications } from "../utils/notifications";
import LocationSelectors from "../components/LocationSelectors";

const steps = ["Contact", "Shipping", "Payment"];

export default function Checkout() {
  const navigate = useNavigate();
  const { cart, cartTotal, clearCart } = useCart();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    deliveryInstructions: "",
  });

  const handlePayment = (e) => {
    e.preventDefault();

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert("Please enter a valid email address");
      return;
    }

    // Create orderDetails outside of the callback
    const orderDetails = {
      orderNumber: "AN-" + Math.floor(Math.random() * 1000000),
      orderDate: new Date().toLocaleDateString(),
      total: cartTotal.toFixed(2),
      subtotal: cartTotal.toFixed(2),
      shipping: "0.00",
      paymentMethod: "Credit Card",
      items: cart.map((item) => ({
        name: item.name,
        quantity: item.quantity,
        price: item.price.toFixed(2),
        image: item.image,
      })),
      shippingAddress: {
        name: `${formData.firstName} ${formData.lastName}`,
        street: formData.address,
        city: formData.city,
        country: "Ghana",
        phone: formData.phone,
      },
      email: formData.email.trim(), // Trim any whitespace
      deliveryInstructions: formData.deliveryInstructions?.trim() || "",
    };

    const handler = window.PaystackPop.setup({
      key: "pk_live_cbf98ad0f8a54b6c6c905c40e2d10a5f9b7c4a1e",
      email: formData.email.trim(),
      amount: cartTotal * 100,
      currency: "GHS",
      ref: "" + Math.floor(Math.random() * 1000000000 + 1),
      callback: function (response) {
        sendOrderNotifications(orderDetails, response)
          .then(() => {
            console.log("Order notification sent successfully");
            clearCart();
            navigate("/order-confirmation", {
              state: { orderDetails },
            });
          })
          .catch((error) => {
            console.error("Error sending order notification:", error);
            // Still navigate to confirmation since payment was successful
            clearCart();
            navigate("/order-confirmation", {
              state: { orderDetails },
            });
          });
      },
      onClose: function () {
        alert("Payment window closed");
      },
    });

    handler.openIframe();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const nextStep = () => {
    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  if (cart.length === 0) {
    return (
      <div className="pt-24">
        <div className="max-w-2xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-serif font-bold text-primary-900 mb-4">
            Your cart is empty
          </h1>
          <p className="text-gray-600 mb-8">
            Add some products to your cart to proceed with checkout.
          </p>
          <Button
            onClick={() => navigate("/products")}
            className="bg-primary-600 text-white px-6 py-3 rounded-md hover:bg-primary-700"
          >
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex justify-center items-center space-x-8">
            {steps.map((step, index) => (
              <div key={step} className="flex items-center">
                <motion.div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                    index <= currentStep
                      ? "bg-gold-500 text-white"
                      : "bg-gray-200 text-gray-500"
                  }`}
                  whileHover={{ scale: 1.1 }}
                >
                  {index + 1}
                </motion.div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-primary-900">{step}</p>
                  {index <= currentStep && (
                    <motion.div
                      className="h-0.5 bg-gold-500 mt-2"
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ duration: 0.5 }}
                    />
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div className="w-24 h-0.5 bg-gray-200 mx-4" />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-soft p-8"
            >
              <form onSubmit={handlePayment} className="space-y-8">
                {/* Contact Information Section */}
                {currentStep === 0 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-6"
                  >
                    <h2 className="text-2xl font-serif font-bold text-primary-900">
                      Contact Information
                    </h2>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="John"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Doe"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="johndoe@example.com"
                        pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="024 123 4567"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </motion.div>
                )}

                {/* Shipping Information Section */}
                {currentStep === 1 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-6"
                  >
                    <h2 className="text-2xl font-serif font-bold text-primary-900">
                      Shipping Information
                    </h2>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Street Address
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="123 Main Street"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <LocationSelectors
                      onLocationChange={({ country, city }) => {
                        setFormData((prev) => ({
                          ...prev,
                          country: country?.label || "",
                          city: city?.label || "",
                        }));
                      }}
                    />

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State / Region
                      </label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        placeholder="Greater Accra"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </motion.div>
                )}

                {currentStep === 2 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-6"
                  >
                    <h2 className="text-2xl font-serif font-bold text-primary-900">
                      Payment Information
                    </h2>
                    <div className="bg-primary-50 rounded-xl p-6">
                      <p className="text-primary-900">
                        You will be redirected to Paystack to complete your
                        payment securely.
                      </p>
                    </div>
                  </motion.div>
                )}

                <div className="flex justify-between pt-6">
                  {currentStep > 0 && (
                    <Button variant="outline" onClick={prevStep}>
                      Previous
                    </Button>
                  )}
                  {currentStep < steps.length - 1 ? (
                    <Button onClick={nextStep}>Next</Button>
                  ) : (
                    <Button type="submit">Complete Payment</Button>
                  )}
                </div>
              </form>
            </motion.div>
          </div>

          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-soft p-8"
          >
            <h2 className="text-2xl font-serif font-bold text-primary-900 mb-6">
              Order Summary
            </h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Order Summary
              </h3>
              {cart.map((item) => (
                <div key={item.id} className="flex justify-between py-2">
                  <div className="flex items-center">
                    <span className="text-gray-600">
                      {item.name} x {item.quantity}
                    </span>
                  </div>
                  <span className="text-gray-900">
                    GHS {(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              ))}
              <div className="border-t border-gray-200 mt-4 pt-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-gray-900">
                    GHS {cartTotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between mt-2">
                  <span className="text-gray-600">Shipping</span>
                  <span className="text-gray-900">GHS 0.00</span>
                </div>
                <div className="flex justify-between mt-4 pt-4 border-t border-gray-200">
                  <span className="text-lg font-medium text-gray-900">
                    Total
                  </span>
                  <span className="text-lg font-medium text-gray-900">
                    GHS {cartTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
